import React from "react";
import PropTypes from "prop-types";

// Panel — one bordered surface holding several sections, separated by dividers.
//
// The cockpit used to be card + card + card + card + table + card + card + card: a border
// around every conceptual group, an uppercase eyebrow on each, and therefore no hierarchy
// at all — everything was emphasised, so nothing was. The rest of the product (Policies,
// Approvals, Forecasts) is one or two dominant surfaces with generous space inside them,
// and this is how the budget page joins them: fewer surfaces, more room, dividers instead
// of borders.
//
// The type scale is deliberately short, and lives here so it cannot drift. Four roles, and
// only one of them is large:
//
//   figure    24px semibold               the authorized amount — ONE per page
//   heading   14px semibold, Title Case   section headings
//   body      14px                        values
//   support   12px                        secondary/supporting text
//   eyebrow   11px medium uppercase       table headers and field labels only
//
// The figure has no constant because there is exactly one of it and it lives on an input
// (see the Authorization section); everything else that states a value does so at body size
// with weight or colour for emphasis. Uppercase is an eyebrow treatment, not a heading
// treatment — reserving it for the smallest role is most of what makes the page quieter.

export const PANEL_HEADING = "text-sm font-semibold text-text";
export const PANEL_SUPPORT = "text-xs text-text-2";
export const PANEL_EYEBROW =
  "text-[11px] font-medium uppercase tracking-wide text-text-2";

export const Panel = ({ className = "", children }) => (
  <div className={`bg-card border border-card-bd rounded-card ${className}`}>{children}</div>
);

// A section inside a Panel. Every section after the first carries the divider, so sections
// can be added, reordered or conditionally omitted without anyone maintaining a "last one
// has no border" rule.
export const PanelSection = ({ title, action, className = "", children }) => (
  <section className={`p-5 border-t border-card-bd first:border-t-0 ${className}`}>
    {title || action ? (
      <div className="flex items-baseline justify-between gap-3 mb-3">
        {title ? <h3 className={PANEL_HEADING}>{title}</h3> : <span />}
        {action}
      </div>
    ) : null}
    {children}
  </section>
);

Panel.propTypes = { className: PropTypes.string, children: PropTypes.node };
PanelSection.propTypes = {
  title: PropTypes.string,
  action: PropTypes.node,
  className: PropTypes.string,
  children: PropTypes.node,
};

export default Panel;
